import { TertiaryButton } from '@ardoq/button';
import { Icon, IconName } from '@ardoq/icons';
import { ToolbarDivider } from '@ardoq/page-layout';
import { dispatchAction } from '@ardoq/rxbeach';
import { pluralizeWithCount } from '@ardoq/common-helpers';
import { t, type I18nLocale } from '@ardoq/i18n';
import { showAllExcludedComponents } from 'loadedState/excludeComponent/actions';

type ShowExcludedButtonProps = {
  excludedComponentCount: number;
  isDiscover: boolean;
  i18nLocale: I18nLocale;
};

/**
 * The standing count of what the datasets are hiding, and the control that puts it back.
 *
 * Rendered by all three toolbar branches rather than only the enabled one. It is the only route
 * back in the product — an exclusion is a traversal filter the viewpoint builder cannot display,
 * and there is no context-menu equivalent — so a branch that drops it leaves components missing
 * with no way to restore them. Two visible datasets and no selection is the disabled branch; any
 * graph selection is the selection branch; both are ordinary states to be in right after
 * excluding something.
 *
 * Not gated by the feature flag, deliberately: the traversal enforces an exclusion for whoever
 * opens the viewpoint, flag or no flag.
 *
 * Gated on `isDiscover`, equally deliberately. That argument is about an editor left with components
 * missing and no control; in Discover the exclusion is not something that happened to the viewer, it
 * is part of the viewpoint they were published. This is the only half of the pair they get - excluding
 * is behind a flag and the component context menu is not a route they are offered - so for them the
 * control is a one-way door: one click un-curates the viewpoint for the rest of the session and only
 * a reload puts it back. The label announcing that the author hid things is its own small leak.
 */
export const ShowExcludedButton = ({
  excludedComponentCount,
  isDiscover,
  i18nLocale,
}: ShowExcludedButtonProps) => {
  if (isDiscover || excludedComponentCount === 0) return null;

  return (
    <>
      <ToolbarDivider />
      <TertiaryButton
        onClick={() => dispatchAction(showAllExcludedComponents())}
      >
        <Icon iconName={IconName.VISIBILITY_OFF} />
        {t(
          i18nLocale,
          'canvas:excludedComponents.showAll',
          { count: excludedComponentCount },
          `Show ${pluralizeWithCount('excluded component', excludedComponentCount)}`
        )}
      </TertiaryButton>
    </>
  );
};
